import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Skapar en engångslänk som loggar in användaren på webben.
 * Appen anropar denna funktion och öppnar sedan länken i systemets webbläsare.
 * Appens egen session påverkas inte.
 */
export const createWebHandoffLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        targetPath: z.string().max(200).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Hämta användarens e-postadress för att generera en magic link.
    const { data: userData, error: userError } = await supabaseAdmin.auth.admin.getUserById(
      context.userId,
    );
    if (userError || !userData.user?.email) {
      throw new Error("Kunde inte hämta användarens e-postadress.");
    }

    const email = userData.user.email;

    // Bygg fullständig återvändo-URL så att Supabase kan skicka användaren
    // tillbaka till webbsidan efter att länken verifierats.
    const request = getRequest();
    const origin = request?.headers.get("origin") ?? "https://sellavo.com";
    const target = data.targetPath?.startsWith("/") ? data.targetPath : "/oversikt";
    const redirectTo = `${origin}/bekraftad?login=1&redirect=${encodeURIComponent(target)}`;

    // Generera en Supabase magic link. Giltighetstiden styrs av projektets
    // inställningar (vanligtvis 1 timme), men länken är engångs.
    const { data: linkData, error: linkError } = await supabaseAdmin.auth.admin.generateLink({
      type: "magiclink",
      email,
      options: { redirectTo },
    });

    const props = linkData?.properties as Record<string, string> | undefined;
    const url = props?.["confirmation_url"] || props?.["action_link"];
    if (linkError || !url) {
      throw new Error(linkError?.message ?? "Kunde inte skapa inloggningslänken.");
    }

    return {
      url,
      email,
    };
  });
